import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Stack,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import type { EnvironmentDocument, UserProfile } from '@finvari/deliver';
import { updateUserProfile } from '../firebase';
import { useVisibleEnvironments } from '../contexts/EnvironmentsContext';

interface UserProfileDialogProps {
  open: boolean;
  onClose: () => void;
  userProfile: UserProfile;
}

const UserProfileDialog: React.FC<UserProfileDialogProps> = ({ open, onClose, userProfile }) => {
  // Same visible set as the deploy pickers / environments list: hides archived and others' personal envs.
  const environmentDocs = useVisibleEnvironments();
  const [displayName, setDisplayName] = useState(userProfile.displayName || '');
  const [githubUsername, setGithubUsername] = useState(userProfile.githubUsername || '');
  const [defaultEnvironmentId, setDefaultEnvironmentId] = useState(userProfile.defaultEnvironmentId || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDisplayName(userProfile.displayName || '');
      setGithubUsername(userProfile.githubUsername || '');
      setDefaultEnvironmentId(userProfile.defaultEnvironmentId || '');
      setError(null);
    }
  }, [open, userProfile]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateUserProfile({
        displayName,
        githubUsername,
        defaultEnvironmentId: defaultEnvironmentId || null,
      });
      onClose();
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const environments: EnvironmentDocument[] = [...environmentDocs].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit Profile</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Display Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            label="GitHub Username"
            value={githubUsername}
            onChange={(e) => setGithubUsername(e.target.value)}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="default-env-label">Default Environment</InputLabel>
            <Select
              labelId="default-env-label"
              label="Default Environment"
              value={defaultEnvironmentId}
              onChange={(e) => setDefaultEnvironmentId(e.target.value)}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {environments.map((env) => (
                <MenuItem key={env.gcpProjectId} value={env.gcpProjectId}>{env.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserProfileDialog;
